import React, { useState, useEffect, useCallback, useRef } from 'react';
import { functions, auth } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { STokenLogo } from '../StreamDisplay';
import './MemoryGame.css';

const SYMBOLS = ['💎', '⭐', '🔷', '🟩', '🎰', '🎡', '🔮', '🌀'];

const ROUND_CONFIG = [
  { round: 1, timeLimit: 60, reward: 25  },
  { round: 2, timeLimit: 45, reward: 50  },
  { round: 3, timeLimit: 30, reward: 100 },
  { round: 4, timeLimit: 15, reward: 200 },
];

function buildDeck() {
  const pairs = [...SYMBOLS, ...SYMBOLS];
  for (let i = pairs.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
  }
  return pairs.map((symbol, index) => ({ id: index, symbol, flipped: false, matched: false }));
}

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function MemoryGame({ onClose }) {
  const [roundIndex, setRoundIndex]   = useState(0);
  const [cards, setCards]             = useState(() => buildDeck());
  const [selected, setSelected]       = useState([]);
  const [locked, setLocked]           = useState(false);
  const [timeLeft, setTimeLeft]       = useState(ROUND_CONFIG[0].timeLimit);
  const [running, setRunning]         = useState(false);
  const [phase, setPhase]             = useState('idle'); // idle | playing | won-round | lost | complete
  const [totalReward, setTotalReward] = useState(0);
  const timerRef                      = useRef(null);

  const config = ROUND_CONFIG[roundIndex];

  // Countdown timer
  useEffect(() => {
    if (running && phase === 'playing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setRunning(false);
            setPhase('lost');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [running, phase]);

  const flipCard = useCallback((index) => {
    if (locked || phase !== 'playing') return;
    if (cards[index].flipped || cards[index].matched) return;
    if (selected.length === 1 && selected[0] === index) return;

    if (!running) {
      setRunning(true);
      setPhase('playing');
    }

    const newCards = cards.map((c, i) => i === index ? { ...c, flipped: true } : c);
    const newSelected = [...selected, index];
    setCards(newCards);
    setSelected(newSelected);

    if (newSelected.length === 2) {
      const [a, b] = newSelected;
      if (newCards[a].symbol === newCards[b].symbol) {
        const matched = newCards.map((c, i) =>
          i === a || i === b ? { ...c, matched: true } : c
        );
        setCards(matched);
        setSelected([]);

        if (matched.every(c => c.matched)) {
          clearInterval(timerRef.current);
          setRunning(false);
          const earned = config.reward;
          setTotalReward(prev => prev + earned);

          if (auth.currentUser) {
            httpsCallable(functions, 'creditGameResult')({ game: 'memory', netStreams: earned })
              .catch(e => console.error('[Memory]', e));
          }

          const nextIndex = roundIndex + 1;
          if (nextIndex >= ROUND_CONFIG.length) {
            setPhase('complete');
          } else {
            setPhase('won-round');
          }
        }
      } else {
        setLocked(true);
        setTimeout(() => {
          setCards(prev => prev.map((c, i) =>
            i === a || i === b ? { ...c, flipped: false } : c
          ));
          setSelected([]);
          setLocked(false);
        }, 1000);
      }
    }
  }, [cards, selected, locked, running, phase, roundIndex, config]);

  const nextRound = () => {
    const next = roundIndex + 1;
    setRoundIndex(next);
    setCards(buildDeck());
    setSelected([]);
    setLocked(false);
    setTimeLeft(ROUND_CONFIG[next].timeLimit);
    setRunning(false);
    setPhase('idle');
  };

  const startGame = () => {
    setRoundIndex(0);
    setCards(buildDeck());
    setSelected([]);
    setLocked(false);
    setTimeLeft(ROUND_CONFIG[0].timeLimit);
    setRunning(false);
    setPhase('idle');
    setTotalReward(0);
  };

  const matchedCount = cards.filter(c => c.matched).length / 2;
  const urgency = timeLeft <= 10 ? 'urgent' : timeLeft <= 20 ? 'warning' : 'normal';

  return (
    <div className="memory">
      <h2 className="memory__title">Memory Game</h2>
      <p className="memory__subtitle" style={{ display:'flex', alignItems:'center', gap:3 }}>
        Round {config.round} of {ROUND_CONFIG.length} · {config.timeLimit}s · +{config.reward}<STokenLogo size={13} />
      </p>

      <div className="memory__hud">
        <div className="memory__stat">
          <span className="memory__stat-label">Pairs</span>
          <span className="memory__stat-value">{matchedCount} / 8</span>
        </div>
        <div className={`memory__timer memory__timer--${urgency}`} aria-label="Time remaining">
          {formatTime(timeLeft)}
        </div>
        <div className="memory__stat memory__stat--right">
          <span className="memory__stat-label">Round</span>
          <span className="memory__stat-value">{config.round} / {ROUND_CONFIG.length}</span>
        </div>
      </div>

      {/* Round progress dots */}
      <div className="memory__rounds">
        {ROUND_CONFIG.map((r, i) => (
          <div
            key={r.round}
            className={`memory__round-dot${i < roundIndex ? ' memory__round-dot--done' : i === roundIndex ? ' memory__round-dot--active' : ''}`}
          />
        ))}
      </div>

      {(phase === 'idle' || phase === 'playing') && (
        <div className="memory__grid" role="grid" aria-label="Memory card grid">
          {cards.map((card, index) => (
            <button
              key={card.id}
              className={`memory__card${card.flipped || card.matched ? ' memory__card--flipped' : ''}${card.matched ? ' memory__card--matched' : ''}`}
              onClick={() => {
                if (phase === 'idle') { setPhase('playing'); setRunning(true); }
                flipCard(index);
              }}
              disabled={card.matched || (locked && !card.flipped)}
              aria-label={card.flipped || card.matched ? card.symbol : 'Hidden card'}
            >
              <div className="memory__card-inner">
                <div className="memory__card-back" aria-hidden="true">
                  <span className="memory__card-back-mark">?</span>
                </div>
                <div className="memory__card-front" aria-hidden={!card.flipped && !card.matched}>
                  <span className="memory__card-symbol">{card.symbol}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {phase === 'won-round' && (
        <div className="memory__round-win" role="status">
          <div className="memory__win-icon">✅</div>
          <h3 className="memory__win-title">Round {config.round} Complete!</h3>
          <div className="memory__win-reward">
            <STokenLogo size={22} />
            <span>+{config.reward}</span>
          </div>
          <p className="memory__win-next">
            Next: {ROUND_CONFIG[roundIndex + 1].timeLimit}s to beat Round {roundIndex + 2}
          </p>
          <button className="btn btn--primary btn--full" onClick={nextRound}>
            Round {roundIndex + 2} →
          </button>
        </div>
      )}

      {phase === 'complete' && (
        <div className="memory__win" role="status">
          <div className="memory__win-icon">🏆</div>
          <h3 className="memory__win-title">All Rounds Complete!</h3>
          <div className="memory__win-reward">
            <STokenLogo size={28} />
            <span>+{totalReward} Total</span>
          </div>
          <button className="btn btn--primary btn--full" style={{ marginTop: 16 }} onClick={startGame}>
            Play Again
          </button>
        </div>
      )}

      {phase === 'lost' && (
        <div className="memory__lost" role="status">
          <div className="memory__win-icon">⏱️</div>
          <h3 className="memory__win-title">Time's Up!</h3>
          <p className="memory__lost-sub">You ran out of time on Round {config.round}.</p>
          {totalReward > 0 && (
            <div className="memory__win-reward">
              <STokenLogo size={22} />
              <span>+{totalReward} earned</span>
            </div>
          )}
          <button className="btn btn--primary btn--full" style={{ marginTop: 16 }} onClick={startGame}>
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}
